import React, { useMemo } from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-calendar-heatmap/dist/styles.css';
import { Box, Typography } from '@mui/material';

// ── Fixed cell-size helpers ────────────────────────────────────────────────
// react-calendar-heatmap renders a viewBox-based SVG with 10-unit squares.
// Setting the container to a computed pixel width keeps every cell at exactly
// TARGET_CELL_PX regardless of how many weeks are in the selected range.
const TARGET_CELL_PX = 13;   // rendered cell size in pixels (matches GitHub ≈13 px)
const LIB_SQUARE     = 10;   // library default squareSize (viewBox units)
const GUTTER_SIZE    = 2;    // gutterSize we pass to <CalendarHeatmap>
const LIB_COL_VB     = LIB_SQUARE + GUTTER_SIZE;  // 12 viewBox-units per column
const SCALE          = TARGET_CELL_PX / LIB_SQUARE; // 1.3 — used to convert vb→px
// The library reserves leftPad + weekdayLabel space in the viewBox (~16 units)
const LIB_ORIGIN_VB  = LIB_SQUARE + GUTTER_SIZE + 4;

const computeNumWeeks = (start, end) => {
  const ms   = new Date(end) - new Date(start);
  const days = Math.round(ms / 86_400_000);
  return Math.ceil((days + 1) / 7) + 1; // +1 for partial-week buffer
};

// ── Constants ─────────────────────────────────────────────────────────────────

const TOOLTIP_ID = 'lc-heatmap-cell-tooltip';

/**
 * Thresholds → CSS class name (applied to every <rect> in the SVG).
 * Override styles are injected via the <style> block below.
 */
const classForValue = (value) => {
  if (!value || value.count === 0) return 'color-empty';
  if (value.count <= 2)  return 'color-scale-1';
  if (value.count <= 5)  return 'color-scale-2';
  if (value.count <= 9)  return 'color-scale-3';
  return 'color-scale-4';
};

/** Legend entries that explain the colour scale to the reader. */
const LEGEND = [
  { label: 'Less',  className: 'color-scale-1' },
  { label: null,    className: 'color-scale-2' },
  { label: null,    className: 'color-scale-3' },
  { label: 'More',  className: 'color-scale-4' },
];

const HEATMAP_STYLES = `
  /* ── Cell colours ──────────────────────────────────────── */
  .react-calendar-heatmap .color-empty   { fill: #ebedf0; }
  .react-calendar-heatmap .color-scale-1 { fill: #9be9a8; }
  .react-calendar-heatmap .color-scale-2 { fill: #40c463; }
  .react-calendar-heatmap .color-scale-3 { fill: #30a14e; }
  .react-calendar-heatmap .color-scale-4 { fill: #216e39; }

  /* ── Hover highlight ───────────────────────────────────── */
  .react-calendar-heatmap rect:hover {
    stroke: #333;
    stroke-width: 1px;
  }

  /* ── Month / weekday labels ────────────────────────────── */
  .react-calendar-heatmap text {
    font-size: 9px;
    fill: #666;
    font-family: inherit;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * LeetcodeHeatmap
 *
 * Renders a GitHub-style calendar heatmap of accepted LeetCode submission
 * activity for a single student.
 *
 * @param {Array<{ date: string, count: number }>} heatmapData - Continuous
 *   daily data (one entry per calendar day — count = 0 for inactive days).
 * @param {string} startDate - 'YYYY-MM-DD'
 * @param {string} endDate   - 'YYYY-MM-DD'
 */
const LeetcodeHeatmap = ({ heatmapData, startDate, endDate }) => {
  // ── Map to CalendarHeatmap value format ───────────────────────────────────
  const values = useMemo(
    () => (heatmapData ?? []).map(({ date, count }) => ({ date, count })),
    [heatmapData]
  );

  // ── Total accepted submissions in the range ──────────────────────────────
  const totalCount = useMemo(
    () => (heatmapData ?? []).reduce((sum, d) => sum + (d.count ?? 0), 0),
    [heatmapData]
  );

  // ── Fixed container width so cells are always TARGET_CELL_PX wide ────────
  const svgWidth = useMemo(() => {
    if (!startDate || !endDate) return 400;
    const weeks = computeNumWeeks(startDate, endDate);
    return Math.round((weeks * LIB_COL_VB + LIB_ORIGIN_VB) * SCALE);
  }, [startDate, endDate]);

  // ── Tooltip attribute builder ────────────────────────────────────────────
  const tooltipDataAttrs = (value) => {
    const base = { 'data-tooltip-id': TOOLTIP_ID };
    if (!value?.date) {
      return { ...base, 'data-tooltip-content': 'No data' };
    }
    const n     = value.count ?? 0;
    const label = n === 1 ? '1 submission' : `${n} submissions`;
    return {
      ...base,
      'data-tooltip-content': `${label} on ${value.date}`,
    };
  };

  return (
    <Box>
      {/* ── Summary strip ────────────────────────────────────────────────── */}
      <Box
        sx={{
          display:        'flex',
          justifyContent: 'space-between',
          alignItems:     'center',
          mb:             1.5,
          flexWrap:       'wrap',
          gap:            1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {totalCount > 0
            ? `${totalCount} accepted submission${totalCount !== 1 ? 's' : ''} in this period`
            : 'No accepted submissions in this period'}
        </Typography>

        {/* ── Legend ──────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Less
          </Typography>
          {/* Empty cell */}
          <Box
            sx={{
              width:        12,
              height:       12,
              borderRadius: 0.5,
              bgcolor:      '#ebedf0',
              border:       '1px solid rgba(0,0,0,0.06)',
            }}
          />
          {LEGEND.map(({ className }, i) => (
            <Box
              key={i}
              className={`react-calendar-heatmap ${className}`}
              component="svg"
              sx={{ width: 12, height: 12, borderRadius: 0.5, overflow: 'hidden' }}
            >
              <rect
                className={className}
                width="12"
                height="12"
                rx="2"
              />
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
            More
          </Typography>
        </Box>
      </Box>

      {/* ── Calendar heatmap ─────────────────────────────────────────────── */}
      {/* Outer box scrolls horizontally on very long ranges; inner box has   */}
      {/* a fixed pixel width derived from the number of weeks so that every  */}
      {/* cell is always TARGET_CELL_PX wide regardless of the chosen range.  */}
      <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <Box
          sx={{
            width:    svgWidth,
            minWidth: svgWidth,
            '& .react-calendar-heatmap': { width: '100%', display: 'block' },
          }}
        >
          <CalendarHeatmap
            startDate={new Date(startDate)}
            endDate={new Date(endDate)}
            values={values}
            classForValue={classForValue}
            tooltipDataAttrs={tooltipDataAttrs}
            showWeekdayLabels
            gutterSize={GUTTER_SIZE}
          />
        </Box>
      </Box>

      {/* ── react-tooltip renderer ───────────────────────────────────────── */}
      <ReactTooltip
        id={TOOLTIP_ID}
        place="top"
        style={{ fontSize: '0.78rem', borderRadius: 6, padding: '4px 10px' }}
      />

      {/* ── Scoped colour-scale styles ───────────────────────────────────── */}
      <style>{HEATMAP_STYLES}</style>
    </Box>
  );
};

export default LeetcodeHeatmap;
